'use client';

/**
 * FormatPrice - Currency-aware price formatting component
 *
 * This is a CORE component that provides consistent price display across all themes.
 * It automatically converts prices to the user's selected currency using the
 * CurrencyContext.
 *
 * Features:
 * - Automatic currency conversion when user switches currency
 * - Locale-aware formatting with proper symbols and separators
 * - Loading state indicator while rates are being fetched
 * - Graceful fallback when conversion is not available
 * - Optional display of original currency
 *
 * Usage:
 * ```tsx
 * // Basic usage - converts to user's selected currency
 * <FormatPrice amount={18000000} currency="MUR" />
 *
 * // With frequency for rentals
 * <FormatPrice amount={5000} currency="USD" frequency="MONTHLY" />
 *
 * // Show original price alongside converted
 * <FormatPrice amount={18000000} currency="MUR" showOriginal />
 * ```
 */

import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import type { Currency } from '@/lib/config/app.config';
import { CURRENCY_CONFIG, DEFAULT_CURRENCY } from '@/lib/config/app.config';

// =============================================================================
// TYPES
// =============================================================================

export type PriceFrequency =
  | 'HOURLY'
  | 'DAILY'
  | 'WEEKLY'
  | 'FORTNIGHTLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'ANNUALLY'
  | 'ONE_TIME';

export interface FormatPriceProps {
  /** Amount to display */
  amount: number | null | undefined;

  /** Currency code of the original amount (ISO 4217) */
  currency: string;

  /** Optional frequency for rental prices */
  frequency?: PriceFrequency | string | null;

  /** Whether to show the original currency alongside converted price */
  showOriginal?: boolean;

  /** Custom text for when price is not available */
  priceOnRequestText?: string;

  /** Custom CSS classes */
  className?: string;

  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /** Whether to abbreviate large numbers (e.g., 1.8M instead of 1,800,000) */
  abbreviate?: boolean;

  /** Custom locale for formatting */
  locale?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get frequency label for rental prices
 */
function getFrequencyLabel(frequency: PriceFrequency | string): string {
  const labels: Record<string, string> = {
    HOURLY: '/hr',
    DAILY: '/day',
    WEEKLY: '/wk',
    FORTNIGHTLY: '/fortnight',
    MONTHLY: '/mo',
    QUARTERLY: '/qtr',
    ANNUALLY: '/yr',
    ONE_TIME: '',
  };
  return labels[frequency] ?? '';
}

/**
 * Abbreviate large numbers
 */
function abbreviateNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(0) + 'K';
  }
  return num.toString();
}

/**
 * Format a price with currency symbol
 */
function formatPriceValue(
  amount: number,
  currency: string,
  locale?: string,
  abbreviate?: boolean
): string {
  const currencyConfig = CURRENCY_CONFIG[currency as Currency];

  if (abbreviate && amount >= 1_000) {
    // For abbreviated, use symbol + abbreviated number
    const symbol = currencyConfig?.symbol ?? currency;
    return `${symbol}${abbreviateNumber(amount)}`;
  }

  // Get the locale to use
  const formatLocale = locale ?? currencyConfig?.locale ?? 'en-US';
  const decimalPlaces = currencyConfig?.decimalPlaces ?? 0;

  try {
    return new Intl.NumberFormat(formatLocale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(amount);
  } catch {
    // Fallback for unsupported currencies
    const symbol = currencyConfig?.symbol ?? currency;
    return `${symbol}${amount.toLocaleString()}`;
  }
}

/**
 * Get size-based CSS classes
 */
function getSizeClasses(size: FormatPriceProps['size']): string {
  switch (size) {
    case 'sm':
      return 'text-sm';
    case 'lg':
      return 'text-xl font-semibold';
    case 'xl':
      return 'text-2xl font-bold';
    case 'md':
    default:
      return 'text-base font-medium';
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * FormatPrice Component
 *
 * Displays prices with automatic currency conversion based on user preference.
 */
export function FormatPrice({
  amount,
  currency,
  frequency,
  showOriginal = false,
  priceOnRequestText = 'Price on request',
  className = '',
  size = 'md',
  abbreviate = false,
  locale,
}: FormatPriceProps) {
  // Get currency context (optional - works outside provider too)
  const currencyContext = useCurrencyOptional();

  // Handle null/undefined amounts
  if (amount == null || amount === 0) {
    return (
      <span className={`${getSizeClasses(size)} text-n3 ${className}`}>
        {priceOnRequestText}
      </span>
    );
  }

  // Normalise currency code
  const originalCurrency = currency.toUpperCase();
  const selectedCurrency = currencyContext?.selectedCurrency ?? DEFAULT_CURRENCY;
  const isConverted = originalCurrency !== selectedCurrency;

  // Convert if needed
  let displayAmount = amount;
  let displayCurrency = originalCurrency;

  if (isConverted && currencyContext?.rates) {
    const converted = currencyContext.convertBetween(
      amount,
      originalCurrency,
      selectedCurrency
    );
    if (converted !== null) {
      displayAmount = converted;
      displayCurrency = selectedCurrency;
    }
  }

  // Format the price
  const formattedPrice = formatPriceValue(
    displayAmount,
    displayCurrency,
    locale,
    abbreviate
  );

  // Get frequency label
  const frequencyLabel = frequency ? getFrequencyLabel(frequency) : '';

  // Size classes
  const sizeClasses = getSizeClasses(size);

  // Show loading indicator if we're trying to convert but rates aren't loaded yet
  const isWaitingForRates =
    isConverted && !currencyContext?.rates && currencyContext?.isLoading;

  if (isWaitingForRates) {
    return (
      <span
        className={`${sizeClasses} text-n3 animate-pulse ${className}`}
        aria-busy="true"
      >
        {formatPriceValue(amount, originalCurrency, locale, abbreviate)}
        {frequencyLabel}
      </span>
    );
  }

  return (
    <span className={`${sizeClasses} ${className}`}>
      <span className="text-n1 dark:text-n8">
        {formattedPrice}
        {frequencyLabel && (
          <span className="text-n3 font-normal">{frequencyLabel}</span>
        )}
      </span>

      {/* Show original price if converted and showOriginal is true */}
      {showOriginal && isConverted && displayCurrency !== originalCurrency && (
        <span className="ml-2 text-n4 text-sm font-normal">
          ({formatPriceValue(amount, originalCurrency, locale, abbreviate)})
        </span>
      )}
    </span>
  );
}

// =============================================================================
// UTILITY EXPORTS
// =============================================================================

/**
 * Format a price without React (for use in server components or utilities)
 * This does NOT perform currency conversion.
 */
export function formatPrice(
  amount: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
  options?: {
    locale?: string;
    abbreviate?: boolean;
    priceOnRequestText?: string;
  }
): string {
  if (amount == null || amount === 0) {
    return options?.priceOnRequestText ?? 'Price on request';
  }

  return formatPriceValue(
    amount,
    currency.toUpperCase(),
    options?.locale,
    options?.abbreviate
  );
}

export default FormatPrice;
